import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { Filter, FilterService } from 'src/app/shared/services/filter/filter.service'
import { firstValueFrom, map, Subscription, take, tap } from 'rxjs'
import { FieldColumnsComponent } from '../../field-columns/field-columns.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { EntitySelectorComponent } from '../../entity-selector/entity-selector.component'
import { AsyncPipe } from '@angular/common'
import {
  EntityRef,
  ServicePointIdReference,
} from 'src/app/shared/models/referenceData'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { ReferenceDataSource } from '../../reference-data/base-reference.service'
import { ReferenceDataService } from '../../reference-data/reference-data.service'
import { ServicePointManagementService } from '../../service-point-management/service-point-management.service'
import { ServicePointSelectorComponent } from '../../service-point-selector/service-point-selector.component'
import { ServicePointAttribute, SpaggAttribute, UpsertAttributesRequest } from '../../service-point-management/model'
import { formatAsUTC, utc } from 'src/app/shared/utilities/date'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'
import { DateRangeFilter, DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    FieldColumnsComponent,
    LabeledPropertyComponent,
    ServicePointSelectorComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
    AsyncPipe,
  ],
  selector: 'app-create-service-point-attributes-modal',
  styles: `
  .only-md {
    display: none;
    @media (min-width: 768px) and (max-width: 991px) {
      display: block;
    }
  }`,
  template: `
    @if(state) {
    <div app-modal-header>{{ actionText }} Service Point Overrides</div>
    <div app-modal-body>
      <div app-field-columns>
        @if(servicePoint('service_point_uid')){
        <div app-labeled-property label="Service Point">
          <div
            app-service-point-selector
            (valueChange)="handleServicePoint($event, 'service_point_uid')"
            [selectedServicePoints]="servicePoint('service_point_uid').servicePoints"
            [slim]="true"
            [config]="servicePoint('service_point_uid').config"
            [allowPartial]="false"
          ></div>
        </div>
        }

        <br class="only-md"/>
        @if(dateRange('effective_date')){
        <div app-labeled-property label="Effective Date">
          <div dateRangeFilterPicker
            [value]="dateRange('effective_date')"
            (valueChange)="handleDate($event, 'effective_date')"
            [showOperation]="dateRange('effective_date').config.showOperation ?? false"
            [showRange]="dateRange('effective_date').config.showRange ?? false"
            [showTime]="dateRange('effective_date').config.showTime ?? false"
            [showSeconds]="dateRange('effective_date').config.showSeconds ?? false"
          ></div>
        </div>
        }
        @if(dateRange('termination_date')){
        <div app-labeled-property label="Termination Date">
          <div dateRangeFilterPicker
            [value]="dateRange('termination_date')"
            (valueChange)="handleDate($event, 'termination_date')"
            [showOperation]="dateRange('termination_date').config.showOperation ?? false"
            [showRange]="dateRange('termination_date').config.showRange ?? false"
            [showTime]="dateRange('termination_date').config.showTime ?? false"
            [showSeconds]="dateRange('termination_date').config.showSeconds ?? false"
          ></div>
        </div>
        }
      </div>
      <hr />
      <p>
        @if(servicePointSelecting) {
          After selecting a service point, review attributes for the service segment. You must provide all
          attributes.
        } @else {
          Provide attributes for the service segment. You must provide all
          attributes.
        }
      </p>
      <div app-field-columns>
        @for(e of entities; track e.name) {
        @if(entity(e.name)){
        <div app-labeled-property [label]="e.display" [disabled]="servicePointSelecting">
          <div
            app-entity-selector
            (valueChange)="handleEntity($event, e.name)"
            [selectedEntities]="entity(e.name).entities"
            [config]="entity(e.name).config"
            [slim]="true"
            [disabled]="servicePointSelecting"
          ></div>
        </div>
        }}
      </div>
    </div>
    <div app-modal-footer>
      <button
      class="action-btn primary"
      [disabled]="submitDisabled$ | async"
        (click)="submit()"
        >
        {{ actionText }}
      </button>
      <button class="action-btn" (click)="onCancel()">Close</button>
    </div>
    }
  `,
})
export class CreateServicePointAttributesModalComponent implements OnDestroy {
  activeModal = inject(NgbActiveModal)
  filterService = inject(FilterService)
  referenceDataService = inject(ReferenceDataService)
  changeDetectorRef = inject(ChangeDetectorRef)
  servicePointManagementService = inject(ServicePointManagementService)

  state: Record<string, Filter | undefined> = {}
  entities: {
    name: string
    display: string
    resource: ReferenceDataSource
  }[] = [
    {
      name: 'ldc_code',
      display: 'LDC',
      resource: 'localDistributionCompanies',
    },
    {
      name: 'eldc_code',
      display: 'ELDC',
      resource: 'localDistributionCompanies',
    },
    {
      name: 'supply_class_code',
      display: 'Supply Class',
      resource: 'supplyClasses',
    },
    {
      name: 'profile_class_code',
      display: 'Profile Class',
      resource: 'profileClasses',
    },
    {
      name: 'retailer_code',
      display: 'Retailer',
      resource: 'retailers',
    },
    {
      name: 'loss_class_code',
      display: 'Loss Class',
      resource: 'lossClasses',
    },
    {
      name: 'location_code',
      display: 'Location',
      resource: 'locations',
    },
    {
      name: 'meter_type_code',
      display: 'Meter Type',
      resource: 'meterTypes',
    },
  ]
  servicePointSelecting = false
  submitDisabled$ = this.filterService.state(CreateServicePointAttributesModalComponent.name).pipe(
    map((state) => {
      const service_point_uid = (
        state['service_point_uid'] as ServicePointFilter | undefined
      )?.state.servicePoints[0]
      const effective_date = (state['effective_date'] as DateRangeFilter | undefined)
        ?.state.value as Date
      const termination_date = (
        state['termination_date'] as DateRangeFilter | undefined
      )?.state.value as Date
      const entity_vals = this.entities.map(
        (entity) => (state[entity.name] as EntityFilter | undefined)?.state.entities[0],
      )
      const required = [
        service_point_uid,
        effective_date,
        termination_date,
        ...entity_vals,
      ]
      this.state = state

      return !required.every(Boolean)
    }),
  )

  private subscription = new Subscription()
  private action: 'CREATE' | 'CLONE' | 'EDIT'

  get actionText(): string {
    switch (this.action) {
      case 'CREATE':
        return 'Create'
      case 'CLONE':
        return 'Clone'
      case 'EDIT':
        return 'Edit'
      default:
        return 'UNKNOWN'
    }
  }

  private onSuccess: () => void = () => void 0

  async init(
    action: typeof this.action,
    data: ServicePointAttribute | undefined,
    onSuccess: () => void = () => void 0,
  ) {
    this.action = action
    this.onSuccess = onSuccess
    this.changeDetectorRef.detectChanges()
    if (action === 'CREATE') {
      this.servicePointSelecting = true
    }
    const { dispatch } = this.filterService
    const entityFilters: EntityFilter[] = []
    for await (const e of this.entities) {
      entityFilters.push(new EntityFilter({
        entities: data && ['CLONE', 'EDIT'].includes(action) 
          ? await firstValueFrom(this.referenceDataService.data(e.resource))
            .then(refs => refs.filter(r =>
              r.entity_code === data[e.name as keyof ServicePointAttribute] as string),
            )
          : [],
        config: {
          resource: e.resource,
          allowMultiple: false,
          meta: {
            fieldGroup: CreateServicePointAttributesModalComponent.name,
            fieldName: e.name,
            fieldDisplay: e.display,
          },
        },
      }, dispatch))
    }

    dispatch(FilterService.InitFiltersAction({
      fieldGroup: CreateServicePointAttributesModalComponent.name,
      fields: {
        'service_point_uid': new ServicePointFilter({
          servicePoints: data && ['CLONE', 'EDIT'].includes(action) ? [{
            service_point_uid: data.service_point_uid, 
            service_point_id: data.service_point_id,
          }]: [],
          config: {
            meta: {
              fieldGroup: CreateServicePointAttributesModalComponent.name,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        'effective_date': new DateRangeFilter({
          value:  data && action === 'EDIT' ? data.effective_date: null,
          operation:  data && action === 'EDIT' ? 'eq' : null,
          config: {
            showOperation: false,
            showRange: false,
            showTime: false,
            showSeconds: false,
            meta: {
              fieldGroup: CreateServicePointAttributesModalComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective Date',
            },
          },
        }, dispatch),
        'termination_date': new DateRangeFilter({
          value:  data && action === 'EDIT' ? data.termination_date: null,
          operation:  data && action === 'EDIT' ? 'eq' : null,
          config: {
            constant: true,
            showOperation: false,
            showRange: false,
            showTime: false,
            showSeconds: false,
            meta: {
              fieldGroup: CreateServicePointAttributesModalComponent.name,
              fieldName: 'termination_date',
              fieldDisplay: 'Termination Date',
            },
          },
        }, dispatch),
        ...entityFilters.reduce((acc, e) => ({ ...acc, [e.state.config.meta.fieldName]: e }), {}),
      },
    }))

    this.changeDetectorRef.detectChanges()
  }

  dateRange(key: string) {
    return (this.state[key] as DateRangeFilter)?.state
  }

  servicePoint(key: string) {
    return (this.state[key] as ServicePointFilter)?.state
  }

  entity(key: string) {
    return (this.state[key] as EntityFilter)?.state
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  onCancel() {
    this.activeModal.dismiss()
  }

  importServicePointEntities(service_point_uid: string) {
    this.servicePointSelecting = true
    const nowUtc = utc()
    this.servicePointManagementService
      .getSpaggAtrributes(
        undefined,
        [
          {
            name: 'service_point_uid',
            values: [{ value: service_point_uid }],
          },
          {
            name: 'effective_date',
            values: [{ value: formatAsUTC(nowUtc, true), operation: '<=' }],
          },
          {
            name: 'termination_date',
            values: [{ value: formatAsUTC(nowUtc, true), operation: '>' }],
          },
        ],
        undefined,
        true,
      )
      .pipe(take(1))
      .subscribe((res) => {
        const sp = res.results.find(r => r.service_point_source_code === 'CSS') ?? res.results[0]
        this.entities.forEach(async (entity) => {
          this.filterService.dispatch(EntityFilter.SetEntityRefsAction({
            entities: await firstValueFrom(
              this.referenceDataService.data(entity.resource, sp[entity.name as keyof SpaggAttribute] as string),
            ),
          }, {
            fieldGroup: CreateServicePointAttributesModalComponent.name,
            fieldName: entity.name,
          }))
          this.changeDetectorRef.detectChanges()
        })
        this.servicePointSelecting = false
      })
  }

  handleServicePoint(
    ref: ServicePointIdReference,
    field: string,
  ) {
    if(this.action === 'CREATE') {
      this.importServicePointEntities(ref.service_point_uid)
    }
    this.filterService.dispatch(
      ServicePointFilter.AddServicePointsAction({
        servicePoints: [ref],
      },
      {
        fieldGroup: CreateServicePointAttributesModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  handleDate(val: DateRangeFilterStateValue, field: string) {
    this.filterService.dispatch(
      DateRangeFilter.SetDateRangeFilterValueAction(val, {
        fieldGroup: CreateServicePointAttributesModalComponent.name,
        fieldName: field as string,
      }),
    )
  }
  

  handleEntity(val: EntityRef, field: string) {
    this.filterService.dispatch(
      EntityFilter.SetEntityRefsAction({
        entities: [val],
      }, {
        fieldGroup: CreateServicePointAttributesModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  async submit() {
    const service_point = (
      this.state['service_point_uid'] as ServicePointFilter
    ).state.servicePoints[0]
    const effective_date = (this.state['effective_date'] as DateRangeFilter).state.value as Date
    const termination_date = (this.state['termination_date'] as DateRangeFilter).state.value as Date
    const entity_vals = this.entities.map((entity) => ({
      [entity.name]: (this.state[entity.name] as EntityFilter)?.state.entities[0]?.entity_code,
    }))
    await firstValueFrom(
      this.servicePointManagementService
        .upsertAttributes(service_point.service_point_id, {
          effective_date,
          termination_date,
          ...entity_vals.reduce((acc, next) => ({ ...acc, ...next }), {}),
        } as UpsertAttributesRequest)
        .pipe(
          tap(() => {
            this.onSuccess()
            this.activeModal.dismiss()
          }),
        ),
    )
  }
}
